import React, { useCallback, useEffect, useState } from "react";
import { useAuth } from "@/auth";
import { useI18n } from "@/i18n";
import { toast } from "sonner";
import { ScrollText } from "lucide-react";
import { fmtMoney, CURRENCIES } from "@/lib/money";
import { PdfLangMenu } from "@/components/PdfLangMenu";
import { statementPDF } from "@/lib/statementPdf";

export const StatementTab = ({ isGlobal, oficinas = [], clients = [], settings = {} }) => {
  const { api } = useAuth();
  const { t } = useI18n();
  const [tipo, setTipo] = useState("general");
  const [entityId, setEntityId] = useState("");
  const [oficina, setOficina] = useState("");
  const [desde, setDesde] = useState("");
  const [hasta, setHasta] = useState("");
  const [moneda, setMoneda] = useState("");
  const [suppliers, setSuppliers] = useState([]);
  const [st, setSt] = useState(null);

  useEffect(() => {
    if (isGlobal) api.get("/suppliers").then(r => setSuppliers(r.data || [])).catch(() => {});
    // eslint-disable-next-line
  }, []);

  const load = useCallback(async () => {
    if ((tipo === "cliente" || tipo === "proveedor") && !entityId) { setSt(null); return; }
    const params = { tipo };
    if (entityId) params.entity_id = entityId;
    if (oficina) params.oficina = oficina;
    if (desde) params.desde = desde;
    if (hasta) params.hasta = hasta;
    if (moneda) params.moneda = moneda;
    try {
      const { data } = await api.get("/accounting/statement", { params });
      setSt(data);
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); setSt(null); }
  }, [api, tipo, entityId, oficina, desde, hasta, moneda]);
  useEffect(() => { load(); }, [load]);

  const cur = st?.moneda || "EUR";
  const money = (v) => fmtMoney(v, cur);
  const inp = "w-full px-3 py-2 border border-slate-300 rounded-lg text-sm outline-none focus:border-indigo-600";
  const lbl = "block text-[11px] font-semibold text-slate-600 mb-1 uppercase tracking-wide";
  const TYPES = ["general", "cliente", ...(isGlobal ? ["proveedor"] : []), "oficina"];

  return (
    <div className="space-y-4" data-testid="acc-statement">
      <div className="bg-white border border-slate-200 rounded-xl p-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
        <div>
          <label className={lbl}>{t("statement_type")}</label>
          <select data-testid="st-type" value={tipo} onChange={(e) => { setTipo(e.target.value); setEntityId(""); }} className={inp}>
            {TYPES.map(k => <option key={k} value={k}>{t(`statement_of_${k}`)}</option>)}
          </select>
        </div>
        {tipo === "cliente" && (
          <div>
            <label className={lbl}>{t("client")}</label>
            <select data-testid="st-client" value={entityId} onChange={(e) => setEntityId(e.target.value)} className={inp}>
              <option value="">—</option>
              {clients.map(c => <option key={c.id} value={c.id}>{c.fullname}{c.empresa ? ` (${c.empresa})` : ""}</option>)}
            </select>
          </div>
        )}
        {tipo === "proveedor" && (
          <div>
            <label className={lbl}>{t("supplier")}</label>
            <select data-testid="st-supplier" value={entityId} onChange={(e) => setEntityId(e.target.value)} className={inp}>
              <option value="">—</option>
              {suppliers.map(s => <option key={s.id} value={s.id}>{s.nombre}</option>)}
            </select>
          </div>
        )}
        {(tipo === "oficina" || tipo === "general") && isGlobal && (
          <div>
            <label className={lbl}>{t("office")}</label>
            <select data-testid="st-office" value={oficina} onChange={(e) => setOficina(e.target.value)} className={inp}>
              <option value="">{tipo === "oficina" ? "—" : t("all_offices")}</option>
              {oficinas.map(o => <option key={o} value={o}>{o}</option>)}
            </select>
          </div>
        )}
        <div><label className={lbl}>{t("from_date")}</label><input type="date" data-testid="st-from" value={desde} onChange={(e) => setDesde(e.target.value)} className={inp} /></div>
        <div><label className={lbl}>{t("to_date")}</label><input type="date" data-testid="st-to" value={hasta} onChange={(e) => setHasta(e.target.value)} className={inp} /></div>
        <div>
          <label className={lbl}>{t("currency")}</label>
          <select data-testid="st-currency" value={moneda} onChange={(e) => setMoneda(e.target.value)} className={inp}>
            <option value="">{cur}</option>
            {CURRENCIES.map(c => <option key={c.code} value={c.code}>{c.code} · {c.symbol}</option>)}
          </select>
        </div>
      </div>

      {!st ? (
        <div className="bg-white border border-slate-200 rounded-xl p-14 text-center text-slate-400">
          <ScrollText className="w-12 h-12 mx-auto opacity-30 mb-3" />
          <p className="text-sm">{t("pick_statement_entity")}</p>
        </div>
      ) : (
        <>
          <div className="flex items-center justify-between flex-wrap gap-2">
            <h3 className="font-display text-base font-semibold">{t("statement")} · {st.titulo} · <span className="font-mono text-sm text-slate-500">{cur}</span></h3>
            <PdfLangMenu testid="st-pdf" onPrint={(lg) => statementPDF(st, lg, settings)} label="PDF" />
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {[
              ["opening_balance", st.saldo_inicial, "bg-slate-50 border-slate-200 text-slate-800"],
              ["total_charges", st.total_cargos, "bg-red-50 border-red-200 text-red-900"],
              ["total_credits", st.total_abonos, "bg-emerald-50 border-emerald-200 text-emerald-900"],
              ["closing_balance", st.saldo_final, "bg-slate-900 border-slate-900 text-white"],
            ].map(([k, v, tone]) => (
              <div key={k} className={`rounded-xl border p-4 ${tone}`} data-testid={`st-${k}`}>
                <div className="text-[11px] font-bold uppercase tracking-wider opacity-70">{t(k)}</div>
                <div className="mt-1.5 text-xl font-semibold tabular-nums">{money(v)}</div>
              </div>
            ))}
          </div>

          <div className="bg-white border border-slate-200 rounded-xl overflow-x-auto">
            <table className="w-full text-sm min-w-[720px]">
              <thead className="bg-slate-50 text-[11px] uppercase text-slate-500">
                <tr>
                  <th className="p-3 text-start">{t("date")}</th>
                  <th className="p-3 text-start">{t("movement")}</th>
                  <th className="p-3 text-start">{t("reference")}</th>
                  <th className="p-3 text-start">{t("concept")}</th>
                  <th className="p-3 text-start">{t("office")}</th>
                  <th className="p-3 text-end">{t("charge")}</th>
                  <th className="p-3 text-end">{t("credit")}</th>
                  <th className="p-3 text-end">{t("balance")}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {st.movimientos.length === 0 && <tr><td colSpan={8} className="p-8 text-center text-slate-400">{t("no_movements")}</td></tr>}
                {st.movimientos.map((m, i) => (
                  <tr key={`${m.fecha}-${m.tipo}-${m.referencia || i}`} data-testid={`st-mov-${i}`}>
                    <td className="p-3 tabular-nums text-slate-600">{m.fecha}</td>
                    <td className="p-3"><span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">{t(`mov_${m.tipo}`) || m.tipo}</span></td>
                    <td className="p-3 font-mono text-xs">{m.referencia || "—"}</td>
                    <td className="p-3">{m.concepto || "—"}</td>
                    <td className="p-3 text-slate-600">{m.oficina || "—"}</td>
                    <td className="p-3 text-end tabular-nums text-red-700">{m.cargo ? money(m.cargo) : "—"}</td>
                    <td className="p-3 text-end tabular-nums text-emerald-700">{m.abono ? money(m.abono) : "—"}</td>
                    <td className="p-3 text-end tabular-nums font-semibold">{money(m.saldo)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};
